import { useRef } from "react";
import type { ContactResult } from "../../../lib/contacts/types";
import type { TrainingAnswer } from "../../../lib/training/types";
import type { KbLane } from "@/lib/kb/types";
import { useFreshAchievementsOptional } from "@/contexts/FreshAchievementsContext";
import { enqueueFeedbackEvent } from "../../../lib/training/feedbackQueue";
import { submitTrainingFeedbackQueue } from "../../../lib/training/feedbackSubmit";
import { runTrainingAsk } from "../../../lib/training/runTrainingAsk";
import { persistTrainingThreadTurn } from "@/lib/training/trainerThreadingClient";
import { classifyProductFromQuery, classifyPurposeFromQuery, laneToPurpose, type GuidanceProduct, type GuidancePurpose, type GuidanceSnapshot } from "./trainingPanelGuidance";

type Params = {
  appVersion: string;
  webhookUrl?: string;
  scopedOrganizationId?: string | null;
  kbLocationId?: string | null;
  locationId?: string;
  locationName?: string;
  salespersonFirstName?: string;
  devImp: any;
  laneSelection: KbLane;
  setLaneSelection: (v: KbLane) => void;
  laneOverrideOnce: KbLane | null;
  purposeSelection: GuidancePurpose | null;
  productSelection: GuidanceProduct | null;
  threadGuidanceById: Record<string, GuidanceSnapshot>;
  askThreadTurns: Array<{ question: string; answer: string; mode: "kb" | "contacts" | "location"; lane: KbLane }>;
  askThreadId: string | null;
  nextAskKind: "new" | "followup";
  setAskThreadTurns: (v: any) => void;
  setAskThreadId: (v: string | null) => void;
  setContacts: (v: ContactResult[]) => void;
  setContactsWhy: (v: any) => void;
  setContactsError: (v: string | null) => void;
  setLocationCards: (v: any[]) => void;
  setLocationError: (v: string | null) => void;
  setLastQuery: (v: string) => void;
  setError: (v: string | null) => void;
  setLoading: (v: boolean) => void;
  setAnswerMode: (v: any) => void;
  setNeedsClarification: (v: boolean) => void;
  setResult: (v: TrainingAnswer | null) => void;
  setResultQuery: (v: string) => void;
  setThreadGuidanceById: (v: any) => void;
  setActiveGuidance: (v: GuidanceSnapshot | null) => void;
  setLaneOverrideOnce: (v: KbLane | null) => void;
  setNextAskKind: (v: "new" | "followup") => void;
  setGuidanceNotice: (v: string | null) => void;
};

export function useTrainingAskFlow(p: Params) {
  const requestSeq = useRef(0);
  const lastAskRef = useRef<{ q: string; at: number } | null>(null);
  const fresh = useFreshAchievementsOptional();
  const ASK_COOLDOWN_MS = 800;

  const onAsk = async (query: string) => {
    const q = query.trim();
    const now = Date.now();
    const nq = q.toLowerCase().replace(/\s+/g, " ").trim();
    const last = lastAskRef.current;
    if (last) {
      const tooSoon = now - last.at < ASK_COOLDOWN_MS;
      const sameQuery = last.q === nq;
      if (tooSoon && sameQuery) return;
    }
    lastAskRef.current = { q: nq, at: now };
    if (!q) return;
    const effectiveLane = p.laneOverrideOnce || p.laneSelection || "answers";
    const isFollowup = p.nextAskKind === "followup" && p.askThreadTurns.length > 0;
    const activeThreadId = isFollowup && p.askThreadId ? p.askThreadId : `thr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    if (!isFollowup) { p.setAskThreadTurns([]); p.setAskThreadId(activeThreadId); }
    const classifierPurpose = classifyPurposeFromQuery(q);
    const classifierProduct = classifyProductFromQuery(q);
    const existingThreadGuidance = isFollowup && !p.laneOverrideOnce ? p.threadGuidanceById[activeThreadId] : undefined;
    const resolvedGuidance: GuidanceSnapshot = existingThreadGuidance ? existingThreadGuidance : { lane: effectiveLane, purpose: p.purposeSelection ?? laneToPurpose(effectiveLane) ?? classifierPurpose, product: p.productSelection ?? classifierProduct, source: p.laneOverrideOnce || p.purposeSelection || p.productSelection ? "user_selected" : "auto_rules" };
    p.setContacts([]); p.setContactsWhy(""); p.setContactsError(null); p.setLocationCards([]); p.setLocationError(null);
    const requestId = ++requestSeq.current;
    p.setLastQuery(q); p.setError(null); p.setLoading(true);
    try {
      const trustLevelOverride = p.devImp.enabled && (p.devImp.trustLevelOverride === 1 || p.devImp.trustLevelOverride === 2 || p.devImp.trustLevelOverride === 3) ? p.devImp.trustLevelOverride : null;
      const data = await runTrainingAsk(q, {
        appVersion: p.appVersion, webhookUrl: p.webhookUrl, organizationId: p.scopedOrganizationId, kbLocationId: p.kbLocationId ?? null, locationId: p.locationId, locationName: p.locationName, salespersonFirstName: p.salespersonFirstName,
        trustLevelOverride, topK: 8, contactFetchLimit: 20, contactLimit: 8, queryKind: isFollowup ? "followup" : "new", threadId: activeThreadId, threadTurn: isFollowup ? p.askThreadTurns.length + 1 : 1, threadContextTurns: isFollowup ? p.askThreadTurns.slice(-4) : [],
        lane: p.laneSelection, laneOverride: p.laneOverrideOnce, guidancePurpose: resolvedGuidance.purpose, guidanceProduct: resolvedGuidance.product, guidanceSource: resolvedGuidance.source,
      });
      p.setAnswerMode(data.answerMode);
      if (requestId !== requestSeq.current) return;
      if (data.fresh_achievements?.length && fresh) {
        fresh.publish(data.fresh_achievements);
      }
      p.setNeedsClarification(data.needsClarification); p.setResult(data.mapped); p.setResultQuery(q);
      p.setContacts((data.contacts || []) as ContactResult[]); p.setContactsWhy(data.contactsWhy || ""); p.setContactsError(data.contactsError);
      p.setLocationCards(data.locationCards || []); p.setLocationError(data.locationError); p.setAskThreadId(activeThreadId); p.setThreadGuidanceById((prev: any) => ({ ...prev, [activeThreadId]: resolvedGuidance }));
      p.setActiveGuidance({ lane: data.effectiveLane ?? resolvedGuidance.lane, purpose: data.guidancePurpose ?? resolvedGuidance.purpose, product: data.guidanceProduct ?? resolvedGuidance.product, source: data.guidanceSource ?? resolvedGuidance.source });
      p.setAskThreadTurns((prev: any) => [...prev, { question: q, answer: data.mapped.answer, mode: data.answerMode, lane: data.effectiveLane ?? resolvedGuidance.lane }].slice(-8));
      p.setLaneOverrideOnce(null); p.setNextAskKind("followup");
      try {
        await persistTrainingThreadTurn({ organizationId: p.scopedOrganizationId, threadId: activeThreadId, query: q, answer: data.mapped.answer, answerMode: data.answerMode, queryKind: isFollowup ? "followup" : "new", needsClarification: data.needsClarification, sources: (data.mapped.hits || []).slice(0, 6).map((hit: any) => ({ id: hit.id, title: hit.title, score: hit.score, url: hit.url })), contacts: (data.contacts || []).slice(0, 6).map((contact: any) => ({ source: contact.source || null, sourceId: contact.sourceId || null, name: contact.name || null, contactPath: contact.contactPath || null })), guidancePurpose: data.guidancePurpose, guidanceProduct: data.guidanceProduct, guidanceSource: data.guidanceSource, confidenceBand: data.confidenceBand, lane: data.effectiveLane ?? resolvedGuidance.lane });
      } catch (persistErr) { console.warn("[training/thread] turn persistence failed", persistErr); }
    } catch (err) {
      if (requestId !== requestSeq.current) return;
      p.setResult(null); p.setResultQuery(""); p.setNeedsClarification(false); p.setError("Couldn’t fetch an answer right now. Please try again.");
      enqueueFeedbackEvent({ version: p.appVersion, query: q, reason: "error", feedbackText: err instanceof Error ? err.message : String(err), bb_tags: [`lane:${effectiveLane}`], locationId: p.locationId, locationName: p.locationName, salespersonFirstName: p.salespersonFirstName });
      if (p.webhookUrl) await submitTrainingFeedbackQueue({ webhookUrl: p.webhookUrl, appVersion: p.appVersion, locationId: p.locationId, locationName: p.locationName, salespersonFirstName: p.salespersonFirstName });
    } finally {
      if (requestId === requestSeq.current) p.setLoading(false);
    }
  };

  const onLaneSelectionChange = (next: KbLane) => {
    p.setLaneSelection(next);
    if (p.askThreadId || p.askThreadTurns.length > 0) {
      p.setAskThreadId(null); p.setAskThreadTurns([]); p.setNextAskKind("new"); p.setGuidanceNotice("Lane changed. Started a new thread to keep retrieval aligned.");
    }
  };

  const onThreadModeButtonClick = () => {
    const hasAskThread = p.askThreadTurns.length > 0;
    if (!hasAskThread) return;
    if (p.nextAskKind === "followup") { p.setNextAskKind("new"); p.setGuidanceNotice("Next question will start a new thread."); }
    else { p.setNextAskKind("followup"); p.setGuidanceNotice(null); }
  };

  return { onAsk, onLaneSelectionChange, onThreadModeButtonClick };
}
